/**
 * SMS/Twilio deployment settings — `Deployment.deploymentSettings` when type is
 * SMS or TWILIO_SMS — with their defaults and factory.
 */

import { z } from "zod";

import { BaseSettingsSchema } from "../Base.schema";
import { PhoneNumberPurchasedSchema } from "./PhoneNumberPurchased.schema";
import { UpfrontActionSchema } from "./UpfrontAction.schema";

/**
 * SMS Deployment Settings Schema
 * Used for SMS, TWILIO_SMS deployment types
 */
export const TwilioDeploymentSettingsSchema = z
  .object({
    // Twilio credentials (legacy - now usually in ApiKeyConnection)
    accountSid: z.string().optional(),
    authToken: z.string().optional(),

    // Upfront actions
    upfrontActions: z.array(UpfrontActionSchema).optional(),

    // WhatsApp Business API ID (for WhatsApp via Twilio)
    wabaId: z.string().optional(),

    // Purchased phone number details
    purchasedNumber: PhoneNumberPurchasedSchema.optional()
  })
  .merge(BaseSettingsSchema);

export type TwilioDeploymentSettings = z.infer<typeof TwilioDeploymentSettingsSchema>;

export const DEFAULT_TWILIO_SETTINGS: Partial<TwilioDeploymentSettings> = {
  upfrontActions: []
};

/**
 * Create SMS/Twilio deployment settings with defaults.
 * Accepts unknown (e.g. raw Prisma JsonValue). Callers should follow up with Zod validation.
 */
export function createTwilioDeploymentSettings(overrides?: unknown): TwilioDeploymentSettings {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return { ...DEFAULT_TWILIO_SETTINGS } as TwilioDeploymentSettings;
  }
  return { ...DEFAULT_TWILIO_SETTINGS, ...overrides } as TwilioDeploymentSettings;
}
